import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Wallet, AlertCircle, CheckCircle, ExternalLink, 
  RefreshCw, DollarSign, Clock, TrendingUp, CreditCard, ArrowRight,
  Calendar, Copy, Link2, Loader2, ChevronDown, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import { HostAppShell } from '../components/HostAppShell';
import { HostDirectModeBanner } from '../components/HostDirectModeBanner';

/**
 * HostPayouts - Earnings & Payouts Financial Hub for hosts
 * Now serves as the SINGLE source of truth for all host money
 * HOST ONLY - Platform owners are blocked
 */

// ============================================================================
// EARNINGS TIMELINE COMPONENT (Moved from Dashboard)
// Uses existing endpoint: GET /api/owner/financial/earnings-timeline
// ============================================================================

const EarningsTimeline = ({ api }) => {
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true); // Default expanded on this page

  useEffect(() => {
    fetchTimeline();
  }, []);

  const fetchTimeline = async () => {
    setLoading(true);
    try {
      const res = await api.get('/owner/financial/earnings-timeline?limit=20');
      setTimeline(res.data);
    } catch (err) {
      console.error('Failed to fetch earnings timeline:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      booking_earning: { text: 'Booking', color: 'bg-green-100 text-green-700', icon: '📅' },
      posthire_earning: { text: 'Post-Hire', color: 'bg-blue-100 text-blue-700', icon: '🔧' },
      extension_earning: { text: 'Extension', color: 'bg-purple-100 text-purple-700', icon: '➕' },
      claim_earning: { text: 'Claim', color: 'bg-amber-100 text-amber-700', icon: '⚖️' }
    };
    return labels[type] || { text: type, color: 'bg-slate-100 text-slate-600', icon: '💰' };
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: <Badge className="bg-green-100 text-green-700 text-[10px]">Paid</Badge>,
      eligible: <Badge className="bg-blue-100 text-blue-700 text-[10px]">Eligible</Badge>,
      scheduled: <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">Scheduled</Badge>,
      pending: <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pending</Badge>,
      settled: <Badge className="bg-green-100 text-green-700 text-[10px]">Settled</Badge>,
      failed: <Badge className="bg-red-100 text-red-700 text-[10px]">Issue</Badge>,
      not_eligible: <Badge className="bg-slate-100 text-slate-500 text-[10px]">Pending</Badge>,
    };
    return badges[status] || <Badge className="bg-slate-100 text-slate-500 text-[10px]">{status}</Badge>;
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    try {
      return new Date(isoString).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
    } catch {
      return '';
    }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <button 
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between text-left"
        >
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            Earnings Timeline
          </CardTitle>
          <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </CardHeader>
      
      {expanded && (
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : timeline ? (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-amber-600 font-medium uppercase">Pending</p>
                  <p className="text-xl font-bold text-amber-700">${timeline.summary?.pending?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-green-600 font-medium uppercase">Paid</p>
                  <p className="text-xl font-bold text-green-700">${timeline.summary?.paid?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-600 font-medium uppercase">Total Earned</p>
                  <p className="text-xl font-bold text-slate-800">${timeline.summary?.total_earned?.toFixed(2) || '0.00'}</p>
                </div>
              </div>

              {/* Earnings List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {timeline.earnings?.length > 0 ? timeline.earnings.map((earning, idx) => {
                  const typeInfo = getTypeLabel(earning.type);
                  return (
                    <Link 
                      key={`${earning.booking_id}-${idx}`}
                      to={`/bookings/${earning.booking_id}`}
                      className="flex items-center justify-between p-3 bg-white border border-slate-100 rounded-lg hover:bg-slate-50 hover:border-slate-200 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full ${typeInfo.color} flex items-center justify-center text-sm`}>
                          {typeInfo.icon}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge className={`${typeInfo.color} text-[10px]`}>{typeInfo.text}</Badge>
                            <span className="text-xs text-slate-400 font-mono">#{earning.booking_id?.slice(-6)}</span>
                          </div>
                          {earning.timestamp && (
                            <p className="text-xs text-slate-400 mt-0.5">{formatDate(earning.timestamp)}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-slate-800">${earning.amount?.toFixed(2)}</span>
                        {getStatusBadge(earning.status)}
                      </div>
                    </Link>
                  );
                }) : (
                  <p className="text-sm text-slate-500 text-center py-8">No earnings yet. Complete your first booking to start earning!</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-8">Unable to load earnings timeline</p>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// ============================================================================

const HostPayouts = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, api } = useAuth();
  
  const [connectStatus, setConnectStatus] = useState(null);
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [nextPayout, setNextPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Dashboard link modal state (fallback for blocked browsers)
  const [dashboardLinkModal, setDashboardLinkModal] = useState({
    open: false,
    url: ''
  });
  const linkInputRef = useRef(null);

  useEffect(() => {
    fetchData();
    
    // Handle return from payout setup
    if (searchParams.get('connected') === 'true') {
      toast.success('Payout onboarding completed! Refreshing status...');
      fetchConnectStatus();
    }
    if (searchParams.get('refresh') === 'true') {
      toast.info('Please continue with payout setup');
    }
  }, [searchParams]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchConnectStatus(),
      fetchPayoutSummary(),
      fetchPayoutHistory(),
      fetchNextPayout()
    ]);
    setLoading(false);
  };

  const fetchConnectStatus = async () => {
    try {
      const res = await api.get('/host/stripe/connect/status');
      setConnectStatus(res.data);
    } catch (error) {
      console.error('Error fetching connect status:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to load payout status');
      }
    }
  };

  const fetchPayoutSummary = async () => {
    try {
      const res = await api.get('/host/payouts/summary');
      setPayoutSummary(res.data);
    } catch (error) {
      console.error('Error fetching payout summary:', error);
    }
  };

  const fetchPayoutHistory = async () => {
    try {
      const res = await api.get('/host/payouts/history');
      setPayoutHistory(res.data.payouts || []);
    } catch (error) {
      console.error('Error fetching payout history:', error);
    }
  };

  const fetchNextPayout = async () => {
    try {
      const res = await api.get('/host/payouts/next');
      setNextPayout(res.data);
    } catch (error) {
      console.error('Error fetching next payout:', error);
    }
  };

  // ===== MANUAL PAYOUT DETAILS FORM STATE =====
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    account_name: '',
    bank_name: '',
    bsb: '',
    account_number: '',
    pay_id: '',
    preferred_method: 'bank_transfer'
  });
  const [payoutFormErrors, setPayoutFormErrors] = useState({});
  const [payoutFormSaving, setPayoutFormSaving] = useState(false);

  const handleStartConnect = async () => {
    // Open the manual payout details form instead of provider onboarding
    setShowPayoutForm(true);
  };

  const validatePayoutForm = () => {
    const errors = {};
    if (!payoutForm.account_name.trim() || payoutForm.account_name.trim().length < 2) {
      errors.account_name = 'Account name is required';
    }
    if (!payoutForm.bank_name.trim() || payoutForm.bank_name.trim().length < 2) {
      errors.bank_name = 'Bank name is required';
    }
    const bsbClean = payoutForm.bsb.replace(/[-\s]/g, '');
    if (!/^\d{6}$/.test(bsbClean)) {
      errors.bsb = 'BSB must be exactly 6 digits';
    }
    const acctClean = payoutForm.account_number.replace(/[-\s]/g, '');
    if (!/^\d{6,10}$/.test(acctClean)) {
      errors.account_number = 'Account number must be 6-10 digits';
    }
    if (payoutForm.preferred_method === 'payid' && !payoutForm.pay_id.trim()) {
      errors.pay_id = 'PayID is required when selected as preferred method';
    }
    setPayoutFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSavePayoutDetails = async () => {
    if (!validatePayoutForm()) return;
    setPayoutFormSaving(true);
    try {
      await api.post('/host/payout-details', {
        account_name: payoutForm.account_name.trim(),
        bank_name: payoutForm.bank_name.trim(),
        bsb: payoutForm.bsb.replace(/[-\s]/g, ''),
        account_number: payoutForm.account_number.replace(/[-\s]/g, ''),
        pay_id: payoutForm.pay_id.trim() || null,
        preferred_method: payoutForm.preferred_method,
      });
      toast.success('Payout details saved successfully!');
      setShowPayoutForm(false);
      // Refresh connect status to reflect new state
      fetchConnectStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payout details');
    } finally {
      setPayoutFormSaving(false);
    }
  };

  const handleRefreshLink = async () => {
    setActionLoading(true);
    try {
      const res = await api.post('/host/stripe/connect/refresh');
      if (res.data.url) {
        if (res.data.link_type === 'dashboard') {
          window.open(res.data.url, '_blank', 'noopener,noreferrer');
          toast.success('Opening payout dashboard...');
        } else {
          window.location.href = res.data.url;
        }
      }
    } catch (error) {
      console.error('Error refreshing link:', error);
      toast.error(error.response?.data?.detail || 'Failed to get payout dashboard link');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetAccount = async () => {
    if (!window.confirm(
      'This will disconnect the current payout account and allow you to set up a new one.\n\nContinue?'
    )) return;
    
    setActionLoading(true);
    try {
      await api.post('/host/stripe/connect/reset');
      toast.success('Payout account disconnected. You can now set up a new payout account.');
      fetchConnectStatus(); // Refresh status — will now show "Not Connected"
    } catch (error) {
      console.error('Error resetting account:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset account');
    } finally {
      setActionLoading(false);
    }
  };

  // Dedicated handler for opening Stripe Express dashboard (connected hosts only)
  // Uses same-tab navigation to avoid Edge/browser popup blocks
  const handleOpenDashboard = async () => {
    setActionLoading(true);
    
    try {
      // Step 1: Fetch fresh login link from backend (uses stripe.Account.create_login_link)
      const res = await api.post('/host/stripe/connect/login_link');
      const url = res.data?.url;
      
      if (!url) {
        toast.error('Failed to get dashboard link');
        setActionLoading(false);
        return;
      }
      
      // Dev logging only
      if (process.env.NODE_ENV === 'development') {
      }
      
      // Step 2: Navigate to Stripe in same tab (avoids Edge popup blocks)
      toast.info('Redirecting to payout dashboard...');
      window.location.assign(url);
      // Note: setActionLoading(false) not needed - page is navigating away
      
    } catch (error) {
      setActionLoading(false);
      console.error('Error opening dashboard:', error);
      if (error.response?.status === 409) {
        // Host not connected, fall back to setup flow
        toast.info('Please complete payout setup first');
        handleStartConnect();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to open payout dashboard');
      }
    }
  };

  // Copy dashboard link to clipboard
  const handleCopyDashboardLink = async () => {
    const url = dashboardLinkModal.url;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard!');
      } else {
        // Fallback: select text in input
        if (linkInputRef.current) {
          linkInputRef.current.select();
          document.execCommand('copy');
          toast.success('Link copied!');
        }
      }
    } catch (err) {
      console.error('Copy failed:', err);
      // Final fallback: select text for manual copy
      if (linkInputRef.current) {
        linkInputRef.current.select();
        toast.info('Press Ctrl+C or Cmd+C to copy');
      }
    }
  };

  // Navigate to dashboard link in same tab (fallback if modal shown)
  const handleOpenDashboardLink = () => {
    const url = dashboardLinkModal.url;
    if (url) {
      toast.info('Redirecting to payout dashboard...');
      window.location.assign(url);
    }
  };

  const handleRefreshStatus = async () => {
    setActionLoading(true);
    try {
      await fetchConnectStatus();
      await fetchNextPayout();
      toast.success('Status refreshed');
    } catch (error) {
      toast.error('Failed to refresh status');
    } finally {
      setActionLoading(false);
    }
  };

  // Block platform owners
  if (user?.is_platform_owner) {
    return (
      <HostAppShell title="Earnings & Payouts" activeId="payouts">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
            <p className="text-slate-500 mb-4">Platform owners access financial tools via the Platform Owner dashboard.</p>
            <Button onClick={() => navigate('/platform-owner')}>Go to Platform Owner Dashboard</Button>
          </CardContent>
        </Card>
      </HostAppShell>
    );
  }

  const isConnected = connectStatus?.connected;
  const isOnboarded = connectStatus?.onboarding_complete;
  const requiresReconnect = connectStatus?.requires_reconnect;
  const stripeEnvironment = connectStatus?.environment;

  // Format currency
  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Format date
  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString('en-AU', { 
        weekday: 'short', 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  // Get readiness badge
  const getReadinessBadge = (readiness) => {
    switch (readiness) {
      case 'ready':
        return <Badge className="bg-green-100 text-green-800">Ready</Badge>;
      case 'pending_verification':
        return <Badge className="bg-orange-100 text-orange-800">Pending Verification</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-800">Setup Required</Badge>;
    }
  };

  return (
    <HostAppShell title="Earnings & Payouts" activeId="payouts">
      <div className="max-w-4xl mx-auto space-y-6">
        <HostDirectModeBanner variant="subtle" context="payouts" />
        {/* Payout Readiness Banner - P0 Fix: Different messaging for pending verification vs action required */}
        {!loading && !isOnboarded && (() => {
          const payoutsEnabled = connectStatus?.payouts_enabled === true;
          const hostActionRequired = connectStatus?.host_action_required === true;
          const isPendingVerification = !payoutsEnabled && !hostActionRequired && isConnected;
          
          if (isPendingVerification) {
            // PENDING VERIFICATION: Payout provider is reviewing, no action from host
            return (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="payout-pending-banner">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-blue-800">Payouts pending activation</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Your account is under review. This usually takes 1-2 business days.
                      <strong> No action is needed from you.</strong> We'll notify you once your payouts are active.
                    </p>
                  </div>
                </div>
              </div>
            );
          }
          
          // ACTION REQUIRED or NOT CONNECTED: Host needs to take action
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="payout-warning-banner">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-amber-800">Add payout details to receive earnings</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    HireHub processes payouts manually while automated payouts are being enabled.
                    Add your payout details below to start receiving earnings.
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004B8D]"></div>
          </div>
        ) : (
          <>
            {/* Next Payout Preview Card */}
            {nextPayout && (
              <Card className="border-slate-100" data-testid="next-payout-card">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading flex items-center gap-2 text-lg">
                    <Calendar className="w-5 h-5 text-[#004B8D]" />
                    Next Payout Preview
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Readiness Status */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Payout Readiness</span>
                    {getReadinessBadge(nextPayout.payout_readiness)}
                  </div>
                  
                  {/* Next Payout Date */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-slate-500">Next Estimated Payout</span>
                      <span className="font-medium text-[#004B8D]">{formatDate(nextPayout.next_payout_at)}</span>
                    </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                      {(nextPayout.paid_amount_cents > 0) && (
                        <div>
                          <p className="text-lg font-bold text-emerald-600">{formatCurrency(nextPayout.paid_amount_cents)}</p>
                          <p className="text-xs text-slate-500">Paid</p>
                        </div>
                      )}
                      {(nextPayout.scheduled_amount_cents > 0) && (
                        <div>
                          <p className="text-lg font-bold text-indigo-600">{formatCurrency(nextPayout.scheduled_amount_cents)}</p>
                          <p className="text-xs text-slate-500">Scheduled</p>
                        </div>
                      )}
                      <div>
                        <p className="text-lg font-bold text-green-600">{formatCurrency(nextPayout.eligible_amount_cents)}</p>
                        <p className="text-xs text-slate-500">Eligible</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-blue-600">{formatCurrency(nextPayout.pending_amount_cents)}</p>
                        <p className="text-xs text-slate-500">Pending</p>
                      </div>
                      {(nextPayout.disputed_amount_cents > 0) && (
                        <div>
                          <p className="text-lg font-bold text-amber-600">{formatCurrency(nextPayout.disputed_amount_cents)}</p>
                          <p className="text-xs text-slate-500">Disputed</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-400 italic">
                    {nextPayout.disclaimer}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* EARNINGS TIMELINE - Financial history of all earnings */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            <EarningsTimeline api={api} />

            {/* Payout Timing Information Card */}
            <Card className="border-slate-100" data-testid="payout-timing-card">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-[#004B8D]" />
                  Payout Timing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <p className="text-sm text-slate-700 leading-relaxed">
                    HireHub releases eligible earnings to your payout account after bookings are completed and claim windows have passed.
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed mt-3">
                    Funds are then transferred to your bank account according to your payout schedule.
                  </p>
                  <p className="text-sm text-slate-600 mt-3 font-medium">
                    HireHub processes payouts manually while automated payouts are being enabled.
                  </p>
                </div>
                
                {isOnboarded && connectStatus?.ui?.state !== 'manual_payout_active' && (
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={handleOpenDashboard}
                    data-testid="payout-settings-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Payout Settings
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Payout Account Status Card */}
            <Card className="border-slate-100" data-testid="connect-status-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-[#004B8D]" />
                  Payout Account
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* ═══════════════════════════════════════════════════════════
                    STATUS + CTA — driven entirely by backend ui metadata.
                    No local state re-derivation.  Backend is the single
                    source of truth for payout state mapping.
                    ═══════════════════════════════════════════════════════════ */}
                {(() => {
                  const ui = connectStatus?.ui;
                  const state = ui?.state || 'no_account';
                  
                  // Icon per state
                  const stateIcons = {
                    no_account:            <Wallet className="w-5 h-5 text-slate-500" />,
                    requires_reconnect:    <AlertCircle className="w-5 h-5 text-red-600" />,
                    onboarding_incomplete: <ArrowRight className="w-5 h-5 text-amber-600" />,
                    action_required:       <AlertCircle className="w-5 h-5 text-amber-600" />,
                    restricted:            <AlertCircle className="w-5 h-5 text-orange-600" />,
                    pending_verification:  <Clock className="w-5 h-5 text-blue-600" />,
                    rejected:              <AlertCircle className="w-5 h-5 text-red-600" />,
                    active:                <CheckCircle className="w-5 h-5 text-green-600" />,
                    manual_payout_active:  <CheckCircle className="w-5 h-5 text-green-600" />,
                    error:                 <AlertCircle className="w-5 h-5 text-red-600" />,
                  };
                  
                  const iconBgColors = {
                    no_account:            'bg-slate-100',
                    requires_reconnect:    'bg-red-100',
                    onboarding_incomplete: 'bg-amber-100',
                    action_required:       'bg-amber-100',
                    restricted:            'bg-orange-100',
                    pending_verification:  'bg-blue-100',
                    rejected:              'bg-red-100',
                    active:                'bg-green-100',
                    manual_payout_active:  'bg-green-100',
                    error:                 'bg-red-100',
                  };
                  
                  const labelColors = {
                    no_account:            'text-slate-700',
                    requires_reconnect:    'text-red-700',
                    onboarding_incomplete: 'text-amber-700',
                    action_required:       'text-amber-700',
                    restricted:            'text-orange-700',
                    pending_verification:  'text-blue-700',
                    rejected:              'text-red-700',
                    active:                'text-green-700',
                    manual_payout_active:  'text-green-700',
                    error:                 'text-red-700',
                  };
                  
                  // Map cta_action to handler
                  const ctaHandlers = {
                    start_connect:  handleStartConnect,
                    reconnect:      handleStartConnect,
                    continue_setup: handleRefreshLink,
                    reset_account:  handleResetAccount,
                    open_dashboard: handleOpenDashboard,
                  };
                  
                  const ctaStyles = {
                    start_connect:  'bg-[#004B8D] hover:bg-[#003a6d]',
                    reconnect:      'bg-red-600 hover:bg-red-700',
                    continue_setup: 'bg-amber-600 hover:bg-amber-700',
                    reset_account:  'bg-red-600 hover:bg-red-700',
                    open_dashboard: '',  // outline variant
                  };
                  
                  const ctaIcons = {
                    start_connect:  <CreditCard className="w-4 h-4 mr-2" />,
                    reconnect:      <CreditCard className="w-4 h-4 mr-2" />,
                    continue_setup: <ArrowRight className="w-4 h-4 mr-2" />,
                    reset_account:  <CreditCard className="w-4 h-4 mr-2" />,
                    open_dashboard: <ExternalLink className="w-4 h-4 mr-2" />,
                  };
                  
                  // Frontend label overrides for friendlier wording
                  const ctaLabelOverrides = {
                    reset_account: 'Reconnect Payout Account',
                  };

                  const ctaAction = ui?.cta_action;
                  const displayLabel = ctaLabelOverrides[ctaAction] || ui?.cta_label;
                  const isOutline = ctaAction === 'open_dashboard';
                  
                  return (
                    <>
                      {/* Status row */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 shrink-0 rounded-full ${iconBgColors[state] || 'bg-slate-100'} flex items-center justify-center`}>
                            {stateIcons[state] || stateIcons.no_account}
                          </div>
                          <div>
                            <p className={`font-medium ${labelColors[state] || 'text-slate-700'}`} data-testid={`status-${state}`}>
                              {ui?.label || 'Unknown'}
                            </p>
                            <p className="text-sm text-slate-500 max-w-md">
                              {ui?.message || ''}
                            </p>
                            {state === 'rejected' && (
                              <p className="text-sm text-slate-500 mt-1">
                                Your bookings and earnings are not affected — you just need to reconnect your payout account.
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge variant="secondary" className={`shrink-0 self-start sm:self-center ${ui?.badge_color || ''}`}>
                          {ui?.badge_label || state}
                        </Badge>
                      </div>
                      
                      {/* CTA row */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        {ui?.cta_label && ctaHandlers[ctaAction] && (
                          <Button
                            className={`w-full sm:flex-1 ${isOutline ? '' : ctaStyles[ctaAction] || ''}`}
                            variant={isOutline ? 'outline' : 'default'}
                            onClick={ctaHandlers[ctaAction]}
                            disabled={actionLoading}
                            data-testid={`cta-${ctaAction}`}
                          >
                            {actionLoading ? (
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              ctaIcons[ctaAction] || null
                            )}
                            {displayLabel}
                          </Button>
                        )}
                        
                        {/* Helper text for reconnect actions */}
                        {ctaAction === 'reset_account' && (
                          <p className="text-xs text-slate-500 w-full sm:w-auto sm:flex-1 -mt-1">
                            Securely reconnect your payout account to continue receiving payouts.
                          </p>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          className="self-end sm:self-auto shrink-0"
                          onClick={handleRefreshStatus}
                          disabled={actionLoading}
                          title="Refresh status"
                          data-testid="refresh-status-btn"
                        >
                          <RefreshCw className={`w-4 h-4 ${actionLoading ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Earnings Summary */}
            {payoutSummary && (
              <Card className="border-slate-100" data-testid="earnings-summary-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      Earnings Summary
                    </CardTitle>
                    {payoutSummary.data_source && (
                      <span className="text-xs text-slate-400 font-mono" data-testid="earnings-data-source">
                        {payoutSummary.data_source}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-green-50 rounded-lg p-4 text-center" data-testid="paid-out-tile">
                      <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                      <p className="text-xl font-bold text-green-700">
                        ${(payoutSummary.paid_out_total ?? payoutSummary.earnings?.paid_out ?? 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-green-600">Paid Out</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 text-center" data-testid="pending-tile">
                      <Clock className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                      <p className="text-xl font-bold text-blue-700">
                        ${(payoutSummary.pending_balance ?? payoutSummary.earnings?.pending ?? 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-blue-600">Pending</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-4 text-center" data-testid="scheduled-tile">
                      <Calendar className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                      <p className="text-xl font-bold text-emerald-700">
                        ${(payoutSummary.scheduled_total ?? 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-emerald-600">Scheduled</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center" data-testid="lifetime-tile">
                      <TrendingUp className="w-5 h-5 text-slate-600 mx-auto mb-1" />
                      <p className="text-xl font-bold text-slate-700">
                        ${(payoutSummary.lifetime_gross ?? payoutSummary.earnings?.lifetime ?? 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-600">Lifetime Gross</p>
                    </div>
                  </div>
                  
                  {/* Disputed amount row (if any) */}
                  {payoutSummary.disputed_total > 0 && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-amber-700 font-medium">Disputed Amount</span>
                        <span className="text-sm font-bold text-amber-700">${payoutSummary.disputed_total.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  
                  {payoutSummary.disputed_not_supported && payoutSummary.disputed_total === 0 && !payoutSummary.data_source?.includes('HireHub') && (
                    <p className="text-xs text-slate-400 mt-3 text-center">
                      Dispute data not available for Express accounts
                    </p>
                  )}
                  
                  {!payoutSummary.connect_ready && connectStatus?.ui?.state !== 'manual_payout_active' && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                      <AlertCircle className="w-4 h-4 inline mr-2" />
                      Add payout details to access your available funds.
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Payout History */}
            <Card className="border-slate-100" data-testid="payout-history-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-600" />
                  Payout History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {payoutHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No payouts yet</p>
                    <p className="text-sm text-slate-400 mt-1">Complete bookings to receive payouts</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {payoutHistory.map((payout, index) => (
                      <div 
                        key={payout.id || payout.payout_id || index}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        data-testid={`payout-item-${index}`}
                      >
                        <div>
                          <p className="font-medium">${payout.amount?.toFixed(2) ?? '0.00'}</p>
                          <p className="text-sm text-slate-500">
                            {payout.created_at || (payout.created ? new Date(payout.created).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A')}
                          </p>
                          {payout.description && (
                            <p className="text-xs text-slate-400 mt-1">{payout.description}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <Badge 
                            variant={payout.status === 'paid' ? 'default' : 'secondary'}
                            className={payout.status === 'paid' ? 'bg-green-100 text-green-800' : payout.status === 'pending' ? 'bg-blue-100 text-blue-800' : ''}
                          >
                            {payout.status}
                          </Badge>
                          {payout.arrival_date && payout.status !== 'paid' && (
                            <p className="text-xs text-slate-400 mt-1">
                              Est. {new Date(payout.arrival_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="text-xs text-slate-400 text-center mt-4">
                  Payouts are processed according to your schedule.
                </p>
              </CardContent>
            </Card>
          </>
        )}

      {/* Dashboard Link Fallback Modal - for mobile browsers that block redirects */}
      <Dialog 
        open={dashboardLinkModal.open} 
        onOpenChange={(open) => setDashboardLinkModal(prev => ({ ...prev, open }))}
      >
        <DialogContent className="sm:max-w-md" data-testid="dashboard-link-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Open Payout Dashboard
            </DialogTitle>
            <DialogDescription>
              Your browser may block automatic redirects. Copy the link below and open it in Chrome or Safari.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              <input
                ref={linkInputRef}
                type="text"
                readOnly
                value={dashboardLinkModal.url}
                className="flex-1 px-3 py-2 text-sm border rounded-md bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={(e) => e.target.select()}
                data-testid="dashboard-link-input"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDashboardLinkModal({ open: false, url: '' })}
              data-testid="dashboard-link-close"
            >
              Close
            </Button>
            <Button 
              variant="outline"
              onClick={handleOpenDashboardLink}
              data-testid="dashboard-link-open"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Link
            </Button>
            <Button 
              onClick={handleCopyDashboardLink}
              data-testid="dashboard-link-copy"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* MANUAL PAYOUT DETAILS FORM DIALOG                             */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Dialog open={showPayoutForm} onOpenChange={setShowPayoutForm}>
        <DialogContent className="sm:max-w-lg" data-testid="manual-payout-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#004B8D]" />
              Payout Details
            </DialogTitle>
            <DialogDescription>
              Enter your Australian bank account details. HireHub will transfer your earnings to this account.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Account Name */}
            <div className="space-y-1.5">
              <Label htmlFor="payout-account-name" className="text-sm font-medium">
                Account Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="payout-account-name"
                placeholder="e.g. John Smith"
                value={payoutForm.account_name}
                onChange={(e) => setPayoutForm(prev => ({ ...prev, account_name: e.target.value }))}
                className={payoutFormErrors.account_name ? 'border-red-400 focus-visible:ring-red-400' : ''}
                data-testid="payout-account-name-input"
              />
              {payoutFormErrors.account_name && (
                <p className="text-xs text-red-500" data-testid="error-account-name">{payoutFormErrors.account_name}</p>
              )}
            </div>

            {/* Bank Name */}
            <div className="space-y-1.5">
              <Label htmlFor="payout-bank-name" className="text-sm font-medium">
                Bank Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="payout-bank-name"
                placeholder="e.g. Commonwealth Bank"
                value={payoutForm.bank_name}
                onChange={(e) => setPayoutForm(prev => ({ ...prev, bank_name: e.target.value }))}
                className={payoutFormErrors.bank_name ? 'border-red-400 focus-visible:ring-red-400' : ''}
                data-testid="payout-bank-name-input"
              />
              {payoutFormErrors.bank_name && (
                <p className="text-xs text-red-500" data-testid="error-bank-name">{payoutFormErrors.bank_name}</p>
              )}
            </div>

            {/* BSB + Account Number row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="payout-bsb" className="text-sm font-medium">
                  BSB <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="payout-bsb"
                  placeholder="e.g. 062-000"
                  maxLength={7}
                  value={payoutForm.bsb}
                  onChange={(e) => setPayoutForm(prev => ({ ...prev, bsb: e.target.value }))}
                  className={payoutFormErrors.bsb ? 'border-red-400 focus-visible:ring-red-400' : ''}
                  data-testid="payout-bsb-input"
                />
                {payoutFormErrors.bsb && (
                  <p className="text-xs text-red-500" data-testid="error-bsb">{payoutFormErrors.bsb}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="payout-account-number" className="text-sm font-medium">
                  Account Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="payout-account-number"
                  placeholder="e.g. 12345678"
                  maxLength={10}
                  value={payoutForm.account_number}
                  onChange={(e) => setPayoutForm(prev => ({ ...prev, account_number: e.target.value }))}
                  className={payoutFormErrors.account_number ? 'border-red-400 focus-visible:ring-red-400' : ''}
                  data-testid="payout-account-number-input"
                />
                {payoutFormErrors.account_number && (
                  <p className="text-xs text-red-500" data-testid="error-account-number">{payoutFormErrors.account_number}</p>
                )}
              </div>
            </div>

            {/* Preferred Payout Method */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Preferred Payout Method</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPayoutForm(prev => ({ ...prev, preferred_method: 'bank_transfer' }))}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 text-left transition-colors ${
                    payoutForm.preferred_method === 'bank_transfer'
                      ? 'border-[#004B8D] bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="payout-method-bank-transfer"
                >
                  <Building2 className={`w-4 h-4 ${payoutForm.preferred_method === 'bank_transfer' ? 'text-[#004B8D]' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${payoutForm.preferred_method === 'bank_transfer' ? 'text-[#004B8D]' : 'text-slate-600'}`}>
                    Bank Transfer
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setPayoutForm(prev => ({ ...prev, preferred_method: 'payid' }))}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 text-left transition-colors ${
                    payoutForm.preferred_method === 'payid'
                      ? 'border-[#004B8D] bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="payout-method-payid"
                >
                  <CreditCard className={`w-4 h-4 ${payoutForm.preferred_method === 'payid' ? 'text-[#004B8D]' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${payoutForm.preferred_method === 'payid' ? 'text-[#004B8D]' : 'text-slate-600'}`}>
                    PayID
                  </span>
                </button>
              </div>
            </div>

            {/* PayID (conditional) */}
            {payoutForm.preferred_method === 'payid' && (
              <div className="space-y-1.5">
                <Label htmlFor="payout-payid" className="text-sm font-medium">
                  PayID <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="payout-payid"
                  placeholder="e.g. email@example.com or 04XX XXX XXX"
                  value={payoutForm.pay_id}
                  onChange={(e) => setPayoutForm(prev => ({ ...prev, pay_id: e.target.value }))}
                  className={payoutFormErrors.pay_id ? 'border-red-400 focus-visible:ring-red-400' : ''}
                  data-testid="payout-payid-input"
                />
                {payoutFormErrors.pay_id && (
                  <p className="text-xs text-red-500" data-testid="error-payid">{payoutFormErrors.pay_id}</p>
                )}
              </div>
            )}

            {/* PayID (optional when bank_transfer is preferred) */}
            {payoutForm.preferred_method === 'bank_transfer' && (
              <div className="space-y-1.5">
                <Label htmlFor="payout-payid-optional" className="text-sm font-medium text-slate-500">
                  PayID <span className="text-xs">(optional)</span>
                </Label>
                <Input
                  id="payout-payid-optional"
                  placeholder="e.g. email@example.com or 04XX XXX XXX"
                  value={payoutForm.pay_id}
                  onChange={(e) => setPayoutForm(prev => ({ ...prev, pay_id: e.target.value }))}
                  data-testid="payout-payid-optional-input"
                />
              </div>
            )}
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowPayoutForm(false)}
              disabled={payoutFormSaving}
              data-testid="payout-form-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="bg-[#004B8D] hover:bg-[#003a6d]"
              onClick={handleSavePayoutDetails}
              disabled={payoutFormSaving}
              data-testid="payout-form-save-btn"
            >
              {payoutFormSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save Payout Details
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </HostAppShell>
  );
};

export default HostPayouts;
